/* eslint-disable react-hooks/exhaustive-deps */

'use client';

import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { ContactStoryblok } from '../../../../../types/storyblok/storyblok-types';
import { InputForm } from '../Inputs/InputForm/InputForm';
import { InputPhone } from '../Inputs/InputPhone/InputPhone';
import { SelectForm } from '../Inputs/SelectForm/SelectForm';

import { Button, Icon, RichText, Typography } from '@/components/core';
import { findValidators } from '@/utils/helper';

import styles from './Form.module.scss';

type FormProps = {
    blok: ContactStoryblok;
};

export const Form: React.FC<FormProps> = ({ blok }) => {
    const {
        name,
        surname,
        email,
        message,
        contactMethod,
        successMessage,
        buttonTitle,
        legalNotice,
        phoneNumber,
        language,
        country,
    } = blok;
    const [responseCode, setResponseCode] = useState(null);
    const [legalChecked, setLegalChecked] = useState(false);
    const [isCompany, setIsCompany] = useState(false);
    const {
        control,
        handleSubmit,
        getValues,
        formState: { isValid },
        reset,
    } = useForm({
        mode: 'onChange',
        criteriaMode: 'firstError',
    });

    const submitForm = async event => {
        if (!isValid || !legalChecked) return;
        event.preventDefault();
        const data = getValues();

        const payload = {
            ...data,
            isCompany,
            marketingConsent: legalChecked,
        };

        const response = await fetch('/api/contact/', {
            method: 'POST',
            body: JSON.stringify(payload),
        });
        setResponseCode(response.status);
    };

    useEffect(() => {
        if (responseCode === 200) {
            reset();
            setTimeout(() => {
                setResponseCode(null);
            }, 4000);
        }
    }, [responseCode]);

    const nameValidators = name?.[0] && findValidators(name?.[0].validators);
    const surnameValidators = surname?.[0] && findValidators(surname?.[0].validators);
    const emailValidators = email?.[0] && findValidators(email?.[0].validators);
    // const scopeValidators = scope?.[0] && findValidators(scope?.[0].validators);
    const messageValidators = message?.[0] && findValidators(message?.[0].validators);
    const phoneValidators = phoneNumber?.[0] && findValidators(phoneNumber?.[0].validators);
    const languageValidators = language?.[0] && findValidators(language?.[0].validators);
    const countryValidators = country?.[0] && findValidators(country?.[0].validators);
    const contactMethodValidators = contactMethod?.[0] && findValidators(contactMethod?.[0].validators);

    const t = useTranslations('Common');

    return (
        <div className={clsx(styles.root, 'container')}>
            <form
                className={styles.form}
                id={blok._uid}
                onSubmit={handleSubmit(submitForm)}
            >
                <div className={styles.inputWrapper}>
                    {name?.[0] && (
                        <Controller
                            control={control}
                            name="name"
                            defaultValue=""
                            rules={{
                                pattern: {
                                    value: new RegExp(nameValidators.pattern?.regexPattern),
                                    message: nameValidators.pattern?.errorMessage,
                                },
                                required: {
                                    value: !!nameValidators.required?.errorMessage,
                                    message: nameValidators.required?.errorMessage,
                                },
                                maxLength: {
                                    value: nameValidators.maxLength?.maxLength,
                                    message: nameValidators.maxLength?.errorMessage,
                                },
                                minLength: {
                                    value: nameValidators.minLength?.minLength,
                                    message: nameValidators.minLength?.errorMessage,
                                },
                            }}
                            render={({ field: { onChange, onBlur, value, ref }, fieldState: { error, isTouched } }) => (
                                <InputForm
                                    blok={name?.[0]}
                                    props={{ onChange, onBlur, value, ref }}
                                    required={!!nameValidators.required}
                                    error={error}
                                    isTouched={isTouched}
                                />
                            )}
                        />
                    )}
                    {surname?.[0] && (
                        <Controller
                            control={control}
                            name="surname"
                            defaultValue=""
                            rules={{
                                pattern: {
                                    value: new RegExp(surnameValidators.pattern?.regexPattern),
                                    message: surnameValidators.pattern?.errorMessage,
                                },
                                required: {
                                    value: !!surnameValidators.required?.errorMessage,
                                    message: surnameValidators.required?.errorMessage,
                                },
                                maxLength: {
                                    value: surnameValidators.maxLength?.maxLength,
                                    message: surnameValidators.maxLength?.errorMessage,
                                },
                                minLength: {
                                    value: surnameValidators.minLength?.minLength,
                                    message: surnameValidators.minLength?.errorMessage,
                                },
                            }}
                            render={({ field: { onChange, onBlur, value, ref }, fieldState: { error, isTouched } }) => (
                                <InputForm
                                    props={{ onChange, onBlur, value, ref }}
                                    blok={surname?.[0]}
                                    required={!!surnameValidators.required}
                                    error={error}
                                    isTouched={isTouched}
                                />
                            )}
                        />
                    )}
                    {email?.[0] && (
                        <Controller
                            control={control}
                            name="email"
                            defaultValue=""
                            rules={{
                                pattern: {
                                    value: new RegExp(emailValidators.pattern?.regexPattern),
                                    message: emailValidators.pattern?.errorMessage,
                                },
                                required: {
                                    value: !!emailValidators.required?.errorMessage,
                                    message: emailValidators.required?.errorMessage,
                                },
                                maxLength: {
                                    value: emailValidators.maxLength?.maxLength,
                                    message: emailValidators.maxLength?.errorMessage,
                                },
                                minLength: {
                                    value: emailValidators.minLength?.minLength,
                                    message: emailValidators.minLength?.errorMessage,
                                },
                            }}
                            render={({ field: { onChange, onBlur, value, ref }, fieldState: { error, isTouched } }) => (
                                <InputForm
                                    props={{ onChange, onBlur, value, ref }}
                                    blok={email?.[0]}
                                    type="email"
                                    required={!!emailValidators.required}
                                    error={error}
                                    isTouched={isTouched}
                                />
                            )}
                        />
                    )}
                    {phoneNumber?.[0] && (
                        <Controller
                            control={control}
                            name="phoneNumber"
                            defaultValue=""
                            rules={{
                                pattern: {
                                    value: new RegExp(phoneValidators.pattern?.regexPattern),
                                    message: phoneValidators.pattern?.errorMessage,
                                },
                                required: {
                                    value: !!phoneValidators.required?.errorMessage,
                                    message: phoneValidators.required?.errorMessage,
                                },
                                maxLength: {
                                    value: phoneValidators.maxLength?.maxLength,
                                    message: phoneValidators.maxLength?.errorMessage,
                                },
                                minLength: {
                                    value: phoneValidators.minLength?.minLength,
                                    message: phoneValidators.minLength?.errorMessage,
                                },
                            }}
                            render={({ field: { onChange, onBlur, value, ref }, fieldState: { error, isTouched } }) => (
                                <InputPhone
                                    blok={phoneNumber?.[0]}
                                    props={{ onChange, onBlur, value, ref }}
                                    error={error}
                                    isTouched={isTouched}
                                />
                            )}
                        />
                    )}
                    {language?.[0] && (
                        <Controller
                            control={control}
                            name="language"
                            defaultValue={null}
                            rules={{
                                pattern: {
                                    value: new RegExp(languageValidators.pattern?.regexPattern),
                                    message: languageValidators.pattern?.errorMessage,
                                },
                                required: {
                                    value: !!languageValidators.required?.errorMessage,
                                    message: languageValidators.required?.errorMessage,
                                },
                                maxLength: {
                                    value: languageValidators.maxLength?.maxLength,
                                    message: languageValidators.maxLength?.errorMessage,
                                },
                                minLength: {
                                    value: languageValidators.minLength?.minLength,
                                    message: languageValidators.minLength?.errorMessage,
                                },
                            }}
                            render={({ field: { onChange, onBlur, value, ref }, fieldState: { error, isTouched } }) => (
                                <SelectForm
                                    props={{ onChange, onBlur, value, ref }}
                                    blok={language?.[0]}
                                    required={!!languageValidators.required}
                                    error={error}
                                    isTouched={isTouched}
                                />
                            )}
                        />
                    )}
                    {country?.[0] && (
                        <Controller
                            control={control}
                            name="country"
                            defaultValue=""
                            rules={{
                                pattern: {
                                    value: new RegExp(countryValidators.pattern?.regexPattern),
                                    message: countryValidators.pattern?.errorMessage,
                                },
                                required: {
                                    value: !!countryValidators.required?.errorMessage,
                                    message: countryValidators.required?.errorMessage,
                                },
                                maxLength: {
                                    value: countryValidators.maxLength?.maxLength,
                                    message: countryValidators.maxLength?.errorMessage,
                                },
                                minLength: {
                                    value: countryValidators.minLength?.minLength,
                                    message: countryValidators.minLength?.errorMessage,
                                },
                            }}
                            render={({ field: { onChange, onBlur, value, ref }, fieldState: { error, isTouched } }) => (
                                <InputForm
                                    props={{ onChange, onBlur, value, ref }}
                                    blok={country?.[0]}
                                    required={!!countryValidators.required}
                                    error={error}
                                    isTouched={isTouched}
                                />
                            )}
                        />
                    )}

                    <div
                        onClick={() => setIsCompany(prev => !prev)}
                        className={styles.isCompany}
                    >
                        <Icon name={isCompany ? 'checkbox' : 'empty_checkbox'} />
                        <Typography
                            looksLike="pSmall"
                            className={styles.legalNotice}
                        >
                            {t('companyFieldLabel')}
                        </Typography>
                    </div>

                    {message?.[0] && (
                        <Controller
                            control={control}
                            name="message"
                            defaultValue=""
                            rules={{
                                pattern: {
                                    value: new RegExp(messageValidators.pattern?.regexPattern),
                                    message: messageValidators.pattern?.errorMessage,
                                },
                                required: {
                                    value: !!messageValidators.required?.errorMessage,
                                    message: messageValidators.required?.errorMessage,
                                },
                                maxLength: {
                                    value: messageValidators.maxLength?.maxLength,
                                    message: messageValidators.maxLength?.errorMessage,
                                },
                                minLength: {
                                    value: messageValidators.minLength?.minLength,
                                    message: messageValidators.minLength?.errorMessage,
                                },
                            }}
                            render={({ field: { onChange, onBlur, value, ref }, fieldState: { error, isTouched } }) => (
                                <InputForm
                                    className={styles.textarea}
                                    type="textarea"
                                    props={{ onChange, onBlur, value, ref }}
                                    blok={message?.[0]}
                                    required={!!messageValidators.required}
                                    error={error}
                                    isTouched={isTouched}
                                />
                            )}
                        />
                    )}
                    {contactMethod?.[0] && (
                        <Controller
                            control={control}
                            name="contactMethod"
                            defaultValue={
                                contactMethod?.[0]?.options && contactMethod?.[0].options.length > 0
                                    ? contactMethod[0].options[contactMethod[0].options.length - 1].content
                                    : null
                            }
                            rules={{
                                pattern: {
                                    value: new RegExp(contactMethodValidators.pattern?.regexPattern),
                                    message: contactMethodValidators.pattern?.errorMessage,
                                },
                                required: {
                                    value: !!contactMethodValidators.required?.errorMessage,
                                    message: contactMethodValidators.required?.errorMessage,
                                },
                                maxLength: {
                                    value: contactMethodValidators.maxLength?.maxLength,
                                    message: contactMethodValidators.maxLength?.errorMessage,
                                },
                                minLength: {
                                    value: contactMethodValidators.minLength?.minLength,
                                    message: contactMethodValidators.minLength?.errorMessage,
                                },
                            }}
                            render={({ field: { onChange, onBlur, value, ref }, fieldState: { error, isTouched } }) => (
                                <SelectForm
                                    props={{ onChange, onBlur, value, ref }}
                                    blok={contactMethod?.[0]}
                                    required={!!contactMethodValidators.required}
                                    error={error}
                                    isTouched={isTouched}
                                />
                            )}
                        />
                    )}
                    <div
                        onClick={() => setLegalChecked(prev => !prev)}
                        className={styles.legalWrapper}
                    >
                        <Icon name={legalChecked ? 'checkbox' : 'empty_checkbox'} />
                        <RichText
                            looksLike="pSmall"
                            document={legalNotice}
                            classNameRoot={styles.legalNotice}
                        />
                    </div>
                </div>

                {buttonTitle && (
                    <div
                        className={clsx(styles.bottomWrap, {
                            [styles.show]: responseCode === 200,
                        })}
                    >
                        <div className={clsx(styles.successMessage)}>
                            <Icon name="check-circle" />
                            <Typography looksLike="pSmall">{successMessage}</Typography>
                        </div>

                        <Button
                            onClick={submitForm}
                            iconName="mail"
                            disabled={!isValid || !legalChecked}
                            className={clsx(styles.button)}
                        >
                            {buttonTitle}
                        </Button>
                    </div>
                )}
            </form>
        </div>
    );
};
